import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CASE_STATUSES, PRIORITIES, statusMeta, formatDate, formatDateTime, compressImage,
  RELACIONES, ESTADOS_VE, AFECTADO_OPTS, ESTADO_ACTUAL_OPTS, SI_NO, MIEMBRO_OPTS, LADO_OPTS,
  NIVEL_INFERIOR_OPTS, NIVEL_SUPERIOR_OPTS, MUNON_OPTS, APOYO_OPTS, ASISTIR_OPTS,
} from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, Save, X, StickyNote, Trash2, Ruler, ImagePlus, Camera, FileDown } from "lucide-react";

const GROUPS = [
  { title: "Persona que registra", fields: [
    ["reg_nombre", "Nombre completo", "text"], ["reg_relacion", "Relación", "select", RELACIONES], ["reg_telefono", "Teléfono", "text"],
  ]},
  { title: "Datos del paciente", fields: [
    ["pac_nombre", "Nombre completo", "text"], ["pac_cedula", "Cédula", "text"], ["pac_edad", "Edad", "number"],
    ["pac_sexo", "Sexo", "select", ["Masculino", "Femenino"]], ["pac_altura", "Altura (cm)", "number"], ["pac_peso", "Peso (kg)", "number"],
    ["pac_ciudad", "Ciudad", "text"], ["pac_estado", "Estado", "select", ESTADOS_VE], ["pac_telefono1", "Teléfono", "text"],
    ["pac_familiar", "Familiar responsable", "text"],
  ]},
  { title: "Información médica", fields: [
    ["afectado_terremoto", "Afectado por terremoto", "select", AFECTADO_OPTS], ["estado_actual", "Estado actual", "select", ESTADO_ACTUAL_OPTS],
    ["ya_amputado", "Ya amputado", "select", SI_NO], ["fecha_amputacion", "Fecha amputación", "date"], ["hospital", "Hospital", "text"],
  ]},
  { title: "Amputación", fields: [
    ["miembro_afectado", "Miembro afectado", "select", MIEMBRO_OPTS], ["lado_afectado", "Lado", "select", LADO_OPTS],
    ["nivel_inferior", "Nivel inferior", "select", NIVEL_INFERIOR_OPTS], ["nivel_superior", "Nivel superior", "select", NIVEL_SUPERIOR_OPTS],
    ["estado_munon", "Estado del muñón", "select", MUNON_OPTS],
  ]},
  { title: "Condición social", fields: [
    ["menor_edad", "Menor de edad", "select", SI_NO], ["apoyo_familiar", "Apoyo familiar", "select", APOYO_OPTS], ["puede_asistir", "Puede asistir", "select", ASISTIR_OPTS],
  ]},
];

const MEDIDAS_FIELDS = [
  ["longitud_munon", "Longitud del muñón (cm)"],
  ["circ_proximal", "Circunferencia proximal (cm)"],
  ["circ_media", "Circunferencia media (cm)"],
  ["circ_distal", "Circunferencia distal (cm)"],
  ["long_contralateral", "Longitud miembro contralateral (cm)"],
  ["circ_referencia", "Circunferencia de referencia (cm)"],
];

const CATEGORIAS = ["Proceso de fabricación", "Prótesis terminada", "Entrega al paciente", "Otro"];

export default function PatientDetail() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [zoom, setZoom] = useState(null);
  const [medidas, setMedidas] = useState({});
  const [savingMedidas, setSavingMedidas] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingReg, setUploadingReg] = useState(false);
  const [photoCat, setPhotoCat] = useState(CATEGORIAS[0]);
  const galleryInputRef = useRef(null);
  const regInputRef = useRef(null);

  const load = () => api.get(`/patients/${numero}`).then(({ data }) => { setP(data); setMedidas(data.medidas || {}); }).catch(() => setP(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [numero]);

  if (p === null) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>;
  if (p === false) return <p className="text-red-600">Paciente no encontrado.</p>;

  const startEdit = () => { setDraft({ ...p }); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const fields = {};
      GROUPS.forEach((g) => g.fields.forEach(([k]) => { if (draft[k] !== p[k]) fields[k] = k.match(/edad|altura|peso/) ? Number(draft[k]) : draft[k]; }));
      const { data } = await api.patch(`/patients/${numero}`, { fields });
      setP(data);
      setEditing(false);
      toast.success("Expediente actualizado");
    } catch {
      toast.error("No se pudo guardar. Intente de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const quickUpdate = async (field, value) => {
    try {
      const { data } = await api.patch(`/patients/${numero}`, { fields: { [field]: value } });
      setP(data);
      toast.success("Actualizado");
    } catch {
      toast.error("No se pudo actualizar.");
    }
  };

  const saveMedidas = async () => {
    setSavingMedidas(true);
    try {
      const { data } = await api.patch(`/patients/${numero}`, { fields: { medidas } });
      setP(data);
      toast.success("Medidas guardadas");
    } catch {
      toast.error("No se pudieron guardar las medidas.");
    } finally {
      setSavingMedidas(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/patients/${numero}/notes`, { text: note.trim() });
      setNote("");
      load();
      toast.success("Nota agregada");
    } catch {
      toast.error("No se pudo agregar la nota.");
    }
  };

  const deletePatient = async () => {
    try {
      await api.delete(`/patients/${numero}`);
      toast.success("Registro eliminado");
      navigate("/admin/pacientes");
    } catch {
      toast.error("No se pudo eliminar el registro.");
    }
  };

  const uploadGalleryPhotos = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setUploadingPhoto(true);
    for (const file of files) {
      try {
        const blob = await compressImage(file, 1600, 0.8);
        const fd = new FormData();
        fd.append("file", blob, file.name.replace(/\.(heic|heif)$/i, ".jpg"));
        await api.post(`/patients/${numero}/photos?categoria=${encodeURIComponent(photoCat)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch {
        toast.error("No se pudo subir una foto.");
      }
    }
    setUploadingPhoto(false);
    load();
    toast.success("Fotografías actualizadas");
  };

  const deleteGalleryPhoto = async (fid) => {
    try {
      await api.delete(`/patients/${numero}/photos/${fid}`);
      load();
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar la foto.");
    }
  };

  const uploadRegPhotos = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setUploadingReg(true);
    for (const file of files) {
      try {
        const blob = await compressImage(file, 1600, 0.8);
        const fd = new FormData();
        fd.append("file", blob, file.name.replace(/\.(heic|heif)$/i, ".jpg"));
        await api.post(`/patients/${numero}/fotos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch {
        toast.error("No se pudo subir una foto.");
      }
    }
    setUploadingReg(false);
    load();
    toast.success("Fotografías del registro actualizadas");
  };

  const deleteRegPhoto = async (fid) => {
    try {
      await api.delete(`/patients/${numero}/fotos/${fid}`);
      load();
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar la foto.");
    }
  };

  const galeria = p.galeria || [];

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/admin/pacientes")} data-testid="back-to-list" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Volver a la lista
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-orange-600">{p.numero_paciente}</h1>
          <p className="text-slate-900 font-medium mt-1">{p.pac_nombre}</p>
          <p className="text-sm text-slate-500">Registrado: {formatDateTime(p.created_at)} · Última modificación: {formatDateTime(p.updated_at)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.open(`${API}/patients/${numero}/pdf?auth=${encodeURIComponent(getToken())}`, "_blank")} data-testid="export-pdf" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium hover:bg-slate-50"><FileDown className="h-4 w-4" /> Exportar PDF</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-expediente" className={`inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-red-300 bg-white text-red-600 font-medium hover:bg-red-50 ${canEdit ? "" : "hidden"}`}><Trash2 className="h-4 w-4" /> Eliminar</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar este registro?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminará permanentemente el expediente de <strong>{p.numero_paciente} — {p.pac_nombre}</strong> y todas sus fotografías. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={deletePatient} data-testid="confirm-delete" className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Case management */}
      <div className="mt-5 bg-white rounded-xl border border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Estado del caso</label>
          <Select value={p.case_status} onValueChange={(v) => quickUpdate("case_status", v)} disabled={!canEdit}>
            <SelectTrigger data-testid="case-status-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {CASE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Prioridad</label>
          <Select value={p.priority || "__none__"} onValueChange={(v) => quickUpdate("priority", v === "__none__" ? "" : v)} disabled={!canEdit}>
            <SelectTrigger data-testid="priority-select" className="h-11 bg-white"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__none__">Sin asignar</SelectItem>
              {PRIORITIES.map((pr) => <SelectItem key={pr.value} value={pr.value}>{pr.value}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="datos" className="mt-5">
        <TabsList className="bg-slate-100 w-full grid grid-cols-3 h-auto p-1">
          <TabsTrigger value="datos" data-testid="tab-datos" className="py-2.5 data-[state=active]:bg-white">Fase 1 · Datos</TabsTrigger>
          <TabsTrigger value="medidas" data-testid="tab-medidas" className="py-2.5 data-[state=active]:bg-white">Fase 2 · Medidas</TabsTrigger>
          <TabsTrigger value="protesis" data-testid="tab-protesis" className="py-2.5 data-[state=active]:bg-white">Fase 3 · Prótesis</TabsTrigger>
        </TabsList>

        {/* ------ FASE 1: DATOS ------ */}
        <TabsContent value="datos" className="mt-5 space-y-5">
          <div className="flex justify-end">
            {!canEdit ? null : !editing ? (
              <button onClick={startEdit} data-testid="edit-expediente" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 bg-white font-medium hover:bg-slate-50"><Pencil className="h-4 w-4" /> Editar datos</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={cancelEdit} className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 bg-white"><X className="h-4 w-4" /> Cancelar</button>
                <button onClick={saveEdit} disabled={saving} data-testid="save-expediente" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-slate-900">Fotografías del registro</h3>
              {canEdit && (
                <button onClick={() => regInputRef.current?.click()} disabled={uploadingReg} data-testid="upload-registro" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm font-medium hover:bg-slate-50 disabled:opacity-60">
                  {uploadingReg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Agregar foto
                </button>
              )}
            </div>
            <div className="flex gap-3 flex-wrap">
              {(p.fotos || []).map((id) => (
                <div key={id} className="relative">
                  <img src={fileUrl(id)} alt="Foto paciente" onClick={() => setZoom(fileUrl(id))} data-testid={`patient-photo-${id}`}
                    className="h-28 w-28 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90" />
                  {canEdit && (
                    <button onClick={() => deleteRegPhoto(id)} data-testid={`delete-registro-${id}`} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center shadow">
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </button>
                  )}
                </div>
              ))}
              {(!p.fotos || p.fotos.length === 0) && <p className="text-sm text-slate-400">Sin fotografías.</p>}
            </div>
            <input ref={regInputRef} type="file" accept="image/*,.heic,.heif" capture="environment" multiple className="hidden" onChange={(e) => { uploadRegPhotos(e.target.files); e.target.value = ""; }} />
          </div>

          {GROUPS.map((g) => (
            <div key={g.title} className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-900 mb-3">{g.title}</h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {g.fields.map(([k, label, type, opts]) => (
                  <div key={k} className="py-2 border-b border-slate-100 last:border-0 sm:border-0">
                    <span className="block text-sm text-slate-500">{label}</span>
                    {editing ? (
                      type === "select" ? (
                        <Select value={draft[k] || "__none__"} onValueChange={(v) => setDraft((d) => ({ ...d, [k]: v === "__none__" ? "" : v }))}>
                          <SelectTrigger data-testid={`edit-${k}`} className="h-11 bg-white mt-1"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                          <SelectContent className="bg-white max-h-72">
                            <SelectItem value="__none__">—</SelectItem>
                            {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <input type={type === "number" ? "number" : type === "date" ? "date" : "text"} value={draft[k] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                          data-testid={`edit-${k}`} className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
                      )
                    ) : (
                      <span className="block text-slate-900 font-medium" data-testid={`field-${k}`}>
                        {type === "date" ? formatDate(p[k]) : (p[k] === "" || p[k] == null ? "—" : String(p[k]))}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* ------ FASE 2: MEDIDAS ------ */}
        <TabsContent value="medidas" className="mt-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-900 mb-1 flex items-center gap-2"><Ruler className="h-5 w-5 text-orange-600" /> Medidas del paciente</h3>
            <p className="text-sm text-slate-500 mb-4">Registre las medidas tomadas para la fabricación de la prótesis.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {MEDIDAS_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
                  <input value={medidas[k] || ""} onChange={(e) => setMedidas((m) => ({ ...m, [k]: e.target.value.replace(/[^\d.]/g, "") }))} data-testid={`medida-${k}`} inputMode="decimal" readOnly={!canEdit}
                    className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600 read-only:bg-slate-50" />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Observaciones de medidas</label>
              <textarea value={medidas.observaciones || ""} onChange={(e) => setMedidas((m) => ({ ...m, observaciones: e.target.value }))} data-testid="medida-observaciones" rows={3} readOnly={!canEdit}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600 read-only:bg-slate-50" />
            </div>
            {canEdit && (
              <button onClick={saveMedidas} disabled={savingMedidas} data-testid="save-medidas" className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-60">
                {savingMedidas ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar medidas
              </button>
            )}
          </div>
        </TabsContent>

        {/* ------ FASE 3: PROTESIS / GALERIA ------ */}
        <TabsContent value="protesis" className="mt-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-900 mb-1 flex items-center gap-2"><ImagePlus className="h-5 w-5 text-orange-600" /> Fotografías del proceso y la prótesis</h3>
            <p className="text-sm text-slate-500 mb-4">Cargue fotografías del proceso de fabricación, la prótesis terminada y la entrega.</p>
            {canEdit && (
              <>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Categoría</label>
                    <Select value={photoCat} onValueChange={setPhotoCat}>
                      <SelectTrigger data-testid="photo-categoria" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => galleryInputRef.current?.click()} disabled={uploadingPhoto} data-testid="upload-gallery" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-60">
                      {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Cargar fotos
                    </button>
                  </div>
                </div>
                <input ref={galleryInputRef} type="file" accept="image/*,.heic,.heif" capture="environment" multiple className="hidden" onChange={(e) => { uploadGalleryPhotos(e.target.files); e.target.value = ""; }} />
              </>
            )}

            <div className="mt-6 space-y-6">
              {galeria.length === 0 && <p className="text-sm text-slate-400">Aún no hay fotografías cargadas.</p>}
              {CATEGORIAS.filter((c) => galeria.some((g) => g.categoria === c)).map((cat) => (
                <div key={cat}>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">{cat}</h4>
                  <div className="flex gap-3 flex-wrap">
                    {galeria.filter((g) => g.categoria === cat).map((g) => (
                      <div key={g.file_id} className="relative group">
                        <img src={fileUrl(g.file_id)} alt={cat} onClick={() => setZoom(fileUrl(g.file_id))} data-testid={`gallery-photo-${g.file_id}`}
                          className="h-28 w-28 object-cover rounded-lg border border-slate-200 cursor-pointer" />
                        <button onClick={() => deleteGalleryPhoto(g.file_id)} data-testid={`delete-gallery-${g.file_id}`}
                          className={`absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center shadow ${canEdit ? "" : "hidden"}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Internal notes */}
      <div className="mt-5 bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2"><StickyNote className="h-5 w-5 text-amber-500" /> Notas internas del equipo</h3>
        {canEdit && (
          <div className="flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" placeholder="Agregar observación…"
              className="flex-1 h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
            <button onClick={addNote} data-testid="add-note" className="h-11 px-4 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700">Agregar</button>
          </div>
        )}
        <div className="mt-4 space-y-3">
          {(p.notes || []).length === 0 && <p className="text-sm text-slate-400">Sin notas aún.</p>}
          {(p.notes || []).slice().reverse().map((n) => (
            <div key={n.id} className="border-l-2 border-amber-300 pl-3 py-1">
              <p className="text-slate-800">{n.text}</p>
              <p className="text-xs text-slate-400 mt-0.5">{n.author} · {formatDateTime(n.created_at)}</p>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent className="max-w-3xl bg-white p-2">
          {zoom && <img src={zoom} alt="Foto ampliada" className="w-full h-auto rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
